import React, { useState, useEffect } from "react";
import { API } from "../../App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

const fromSite = (site) => ({
  client_name: site?.client_name || "",
  client_email: site?.client_email || "",
  client_phone: site?.client_phone || "",
  client_notes: site?.client_notes || "",
});

export function AddClientDialog({ open, onClose, site, onSaved }) {
  const [form, setForm] = useState(fromSite(site));
  const [saving, setSaving] = useState(false);
  const token = () => localStorage.getItem("token");
  const isEdit = !!site?.client_name;

  useEffect(() => {
    if (open) setForm(fromSite(site));
  }, [open, site]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.client_name.trim()) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/sites/${site.id}/client`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || "Could not save client");
      }
      const updatedSite = await res.json();
      toast.success(isEdit ? "Client updated" : "Client added to site");
      onSaved(updatedSite);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="add-client-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Client" : "Add Client"} — {site?.name}
          </DialogTitle>
          <DialogDescription>
            Saved to this site and reused for conversations & variations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Client name *</Label>
            <Input
              value={form.client_name}
              onChange={(e) => set("client_name", e.target.value)}
              data-testid="client-name-input"
            />
          </div>
          <div>
            <Label>Client email</Label>
            <Input
              type="email"
              value={form.client_email}
              onChange={(e) => set("client_email", e.target.value)}
              data-testid="client-email-input"
            />
          </div>
          <div>
            <Label>Client phone</Label>
            <Input
              value={form.client_phone}
              onChange={(e) => set("client_phone", e.target.value)}
              data-testid="client-phone-input"
            />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              rows={3}
              value={form.client_notes}
              onChange={(e) => set("client_notes", e.target.value)}
              placeholder="Anything useful — preferred contact method, etc."
              data-testid="client-notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="client-save-btn">
            {saving ? "Saving..." : isEdit ? "Save Client" : "Add Client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
